/**
 * Request-flow editor chrome.
 * Renders the fixed top bar, primary save/send actions, and the inline suggestion navigator.
 */
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronUp,
  faSpinner,
  faSave,
  faCheckCircle,
  faPaperPlane,
  faFileSignature,
} from '@fortawesome/free-solid-svg-icons';

import { INLINE_SUGGESTION_VIEW_MODES } from './userDbqEditorRequestFlowUtils';

const UserDbqEditorRequestFlowChrome = ({
  dbqStyles,
  dbqStandaloneStyles,
  fixedHeaderStyles,
  title,
  showSaveSuccess,
  useInlineSuggestionMode,
  inlineSuggestionViewMode,
  onChangeInlineSuggestionViewMode,
  dismissedSuggestionCount = 0,
  onOpenDismissedSuggestions,
  isSaving,
  onSave,
  canProviderSubmit,
  onSignAndSubmit,
  providerSignActionLabel,
  canManageRequestWorkspace,
  canMarkReadyForReview,
  isMarkingReadyForReview,
  onMarkReadyForReview,
  reviewActionLabel,
  isInlineSuggestionsView,
  inlineSuggestionNavigatorCount = 0,
  isSuggestionCountLoading,
  activeInlineSuggestionNavigatorIndex = -1,
  activeInlineSuggestionNavigatorLabel = '',
  onInlineSuggestionNavigatorCurrent,
  onInlineSuggestionNavigatorMove,
}) => (
  <>
    <style>{dbqStyles}</style>
    <style>{dbqStandaloneStyles}</style>
    <style>{fixedHeaderStyles}</style>

    <header className="dbq-fixed-header">
      <div className="dbq-fixed-header-inner">
        <div className="dbq-fixed-header-logo">
          <img
            src={process.env.PUBLIC_URL + '/VR-Full-White-Horizontal-01.png'}
            alt="Veritas Ratings"
            title="Veritas Ratings"
          />
        </div>
        <div className="dbq-fixed-header-title">
          <h1>{title}</h1>
          <div className="dbq-header-autosave">
            <span className="dbq-header-autosave-dot" />
            Auto-saving
          </div>
        </div>
        <div className="dbq-fixed-header-actions">
          {showSaveSuccess ? (
            <span className="dbq-header-success">
              <FontAwesomeIcon icon={faCheckCircle} />
              Saved
            </span>
          ) : null}
          {useInlineSuggestionMode ? (
            <div className="dbq-header-review-switch" role="group" aria-label="DBQ review view">
              <button
                type="button"
                className={inlineSuggestionViewMode === INLINE_SUGGESTION_VIEW_MODES.suggestions ? 'is-active' : ''}
                onClick={() => onChangeInlineSuggestionViewMode(INLINE_SUGGESTION_VIEW_MODES.suggestions)}
              >
                Suggestions
              </button>
              <button
                type="button"
                className={inlineSuggestionViewMode === INLINE_SUGGESTION_VIEW_MODES.currentDbq ? 'is-active' : ''}
                onClick={() => onChangeInlineSuggestionViewMode(INLINE_SUGGESTION_VIEW_MODES.currentDbq)}
              >
                Current DBQ
              </button>
            </div>
          ) : null}
          {useInlineSuggestionMode && dismissedSuggestionCount > 0 ? (
            <button
              type="button"
              className="dbq-header-dismissed-btn"
              onClick={onOpenDismissedSuggestions}
            >
              Dismissed ({dismissedSuggestionCount})
            </button>
          ) : null}
          <button
            type="button"
            className="dbq-header-btn dbq-header-btn-save"
            onClick={() => onSave(false)}
            disabled={isSaving}
          >
            {isSaving ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              <FontAwesomeIcon icon={faSave} />
            )}
            <span>Save</span>
          </button>
          {canProviderSubmit ? (
            <button
              type="button"
              className="dbq-header-btn dbq-header-btn-sign"
              onClick={onSignAndSubmit}
            >
              <FontAwesomeIcon icon={faFileSignature} />
              <span>{providerSignActionLabel}</span>
            </button>
          ) : null}
          {canManageRequestWorkspace && canMarkReadyForReview ? (
            <button
              type="button"
              className="dbq-header-btn dbq-header-btn-send"
              onClick={onMarkReadyForReview}
              disabled={isMarkingReadyForReview}
            >
              {isMarkingReadyForReview ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <FontAwesomeIcon icon={faPaperPlane} />
              )}
              <span>{reviewActionLabel}</span>
            </button>
          ) : null}
        </div>
      </div>
    </header>

    {isInlineSuggestionsView ? (
      <div
        className="dbq-inline-suggestion-navigator"
        role="navigation"
        aria-label="Suggestion navigation"
      >
        <div className="dbq-inline-suggestion-navigator-copy">
          <span className="dbq-inline-suggestion-navigator-label">Suggestions</span>
          <button
            type="button"
            className="dbq-inline-suggestion-navigator-current"
            onClick={onInlineSuggestionNavigatorCurrent}
            disabled={inlineSuggestionNavigatorCount === 0}
            title={activeInlineSuggestionNavigatorLabel}
            aria-label={
              isSuggestionCountLoading
                ? 'Suggestions are still loading'
                : inlineSuggestionNavigatorCount > 0
                ? activeInlineSuggestionNavigatorIndex >= 0
                  ? `Go to suggestion ${activeInlineSuggestionNavigatorIndex + 1} of ${inlineSuggestionNavigatorCount}: ${activeInlineSuggestionNavigatorLabel}`
                  : `No suggestion selected. Use next to go to suggestion 1 of ${inlineSuggestionNavigatorCount}.`
                : 'No suggestions in form'
            }
          >
            {isSuggestionCountLoading ? (
              <span
                className="dbq-inline-suggestion-navigator-loading"
                title="Suggestions are still loading"
              >
                <FontAwesomeIcon icon={faSpinner} spin />
              </span>
            ) : (
              inlineSuggestionNavigatorCount > 0
                ? `${activeInlineSuggestionNavigatorIndex >= 0 ? activeInlineSuggestionNavigatorIndex + 1 : '-'} / ${inlineSuggestionNavigatorCount}`
                : '0'
            )}
          </button>
        </div>
        <div className="dbq-inline-suggestion-navigator-actions">
          <button
            type="button"
            className="dbq-inline-suggestion-navigator-btn"
            onClick={() => onInlineSuggestionNavigatorMove('previous')}
            disabled={inlineSuggestionNavigatorCount === 0}
            aria-label="Previous suggestion"
            title="Previous suggestion"
          >
            <FontAwesomeIcon icon={faChevronUp} />
          </button>
          <button
            type="button"
            className="dbq-inline-suggestion-navigator-btn"
            onClick={() => onInlineSuggestionNavigatorMove('next')}
            disabled={inlineSuggestionNavigatorCount === 0}
            aria-label="Next suggestion"
            title="Next suggestion"
          >
            <FontAwesomeIcon icon={faChevronDown} />
          </button>
        </div>
      </div>
    ) : null}

    <div className="dbq-header-spacer" />
  </>
);

export default UserDbqEditorRequestFlowChrome;
